import { Observable, OperatorFunction, ReplaySubject, filter, map, pairwise, share, startWith } from 'rxjs';
import { isExists } from './is.utils';

export interface FieldDiff<T> {
  oldValue: T;
  newValue: T;
}

export type Diff<T> = {
  [K in keyof T]: FieldDiff<T[K]>;
};

export interface DiffWithState<T> {
  diff: Diff<T>;
  newState: T;
}

export function getDiff<T extends Object>(oldData: T, newData: T, fields: (keyof T)[]): Diff<T> {
  return fields.reduce((diff, field) => {
    if (oldData[field] !== newData[field]) {
      diff[field] = {
        oldValue: oldData[field],
        newValue: newData[field],
      };
    }
    return diff;
  }, Object.create(oldData.constructor) as Diff<T>);
}

export function getFullDiff<T extends Object>(target: T, source: T): Diff<T> {
  return getDiff(target, source, Object.keys(target) as Array<keyof T>);
}

export function isConvertedToNumber(input: string): boolean {
  return !isExists(input) && !Number.isNaN(Number(input));
}

export function diffObject<T extends Object>(...fields: (keyof T)[]): OperatorFunction<T, DiffWithState<T>> {
  return (source) =>
    source.pipe(
      startWith({} as T),
      pairwise<T>(),
      map(([oldVal, newVal]: [T, T]) => ({ newState: newVal, diff: getDiff(oldVal, newVal, fields) })),
      filter(({ diff }) => Object.keys(diff).length > 0),
    );
}

// NOTE: replacement of publishReplay(1) + refCount()
export function publishRef<T>(source$: Observable<T>) {
  const replaySubject = new ReplaySubject<T>(1);
  return source$.pipe(
    share({
      connector: () => replaySubject,
      resetOnError: false,
      resetOnComplete: false,
      resetOnRefCountZero: true,
    }),
  );
}
